import React from "react";
import { ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import { DateTime } from "luxon";

import DelayInput from "../DelayInput";

import { V2 } from "../../Api";
import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, truncateEllipse } from "../../Utilities";
import { IAuthProps } from "../AuthContext";
import withAuth from "../withAuth";
import withTitle from "../withTitle";

import "../../table.css";
import "../viewer.css";

interface ISurgeryColorListViewState {
    appointments: any[];
    dateMode: "for" | "on";

    locations: any[];
    officeLocations: any[];
    searchText: string;
    searchLocation: string;
    searchOfficeLocation: string;
    searchOffset: number;
    // searchPerson: string;
    searchStatus: string;

    searchEnd: string;
    searchStart: string;

    isLoading: boolean | string;

    [name: string]: any;
}

const PAGE_SIZE = 50;

class SurgeryColorListView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, ISurgeryColorListViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        this.state = {
            appointments: [],
            dateMode: props.location.state ? props.location.state.dateMode : "on",

            locations: [],
            officeLocations: [],
            searchLocation: props.location.state ? props.location.state.searchLocation : "",
            searchOfficeLocation: props.location.state ? props.location.state.searchOfficeLocation : "",
            searchOffset: props.location.state && props.location.state.searchOffset ? props.location.state.searchOffset : 0,
            searchStatus: props.location.state ? props.location.state.searchStatus : "",
            searchText: props.location.state ? props.location.state.searchText : "",

            searchEnd: props.location.state ? props.location.state.searchEnd : "",
            searchStart: props.location.state ? props.location.state.searchStart : "",

            isLoading: true
        };

        this.handleElementChange = this.handleElementChange.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.previousPage = this.previousPage.bind(this);
    }

    public async componentDidMount() {
        try {
            const [locations, officeLocations] = await Promise.all([
                V2.get<any[]>("locations?type=surgery"),
                V2.get<any[]>("locations?type=office")
            ]);


            this.setState({
                locations,
                officeLocations,
            }, () => this.search());
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        let table;

        if (this.state.isLoading === true) {
            table = <div className="spinner-center lds-dual-ring" />;
        }
        else if (typeof this.state.isLoading === "string") {
            table = <span className="error-text">{this.state.isLoading}</span>;
        }
        else {
            let tbody;

            if (this.state.appointments.length === 0) {
                tbody = (
                    <tr>
                        <td colSpan={10}>No appointments found</td>
                    </tr>
                );
            }
            else {
                tbody = this.state.appointments.map(a => (
                    <tr key={a.id} className={this.statusToClass(a.status)}>
                        <td data-caption="Date Created: ">
                            {formatDateTimeLocalNumeric(a.creationTimestamp)}<br />
                            {!DateTime.fromISO(a.creationTimestamp).hasSame(DateTime.fromISO(a.orderedDate), "day")
                                ? <small>Ordered {formatDateLocalNumeric(a.orderedDate)}</small>
                                : null}
                        </td>
                        <td data-caption="Name: " className="wrap">{formatName(a.patient.name)}<br /><small>(#{a.patient.chartNumber})</small></td>
                        {/*<td data-caption="Chart #: ">{a.patient.chartNumber}</td>*/}
                        <td data-caption="Type: " className="wrap">{a.primarySurgery.shortName}</td>
                        <td data-caption="Provider: " className="wrap">{formatName(a.provider.name)}</td>
                        <td data-caption="Scheduled For: ">{a.time ? formatDateTimeLocalNumeric(DateTime.fromISO(`${a.date.substring(0, 10)}T${a.time}`, { zone: "America/New_York" })) : formatDateLocalNumeric(Date.fromLocalDate(a.date.substring(0, 10)))}</td>
                        <td data-caption="Office Visit: ">{a.officeLocation.shortName || a.officeLocation.name}</td>
                        <td data-caption="Note: " className="wrap">{truncateEllipse(a.note, 40)}</td>
                        <td>
                            <Link className="button small" to={`/surgery/${a.id}`}>View</Link>
                        </td>
                    </tr>
                ));
            }

            table = (
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th>Created On</th>
                            <th>Name</th>
                            {/*<th>Chart #</th>*/}
                            <th>Surgery</th>
                            <th>Provider</th>
                            <th>Scheduled For</th>
                            <th>Office Visit</th>
                            <th>Note</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>Surgery Appointments</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tbody}
                    </tbody>
                </table>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <div className="filter-control-box">
                    <select name="dateMode" value={this.state.dateMode} onChange={this.handleElementChange}>
                        <option value="for">Scheduled For</option>
                        <option value="on">Created On</option>
                        <option value="ordered">Ordered On</option>
                    </select>
                    <div className="control-group fill-space">
                        <span>Start Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchStart} name="searchStart" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <div className="control-group fill-space">
                        <span>End Date:</span>
                        <DelayInput type="date" timeout={500} value={this.state.searchEnd} name="searchEnd" onChangeDelayed={this.handleElementChange} />
                    </div>
                    <select value={this.state.searchStatus} name="searchStatus" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All</option>
                        <option value="canceled,noshow,notneeded">Any Canceled</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="complete">Completed</option>
                        <option value="noshow">Soft-canceled</option>
                        <option value="canceled">Canceled</option>
                        <option value="canceled,noshow">Soft & Hard Cancels</option>
                        <option value="notneeded">Canceled - Not Needed</option>
                        <option value="failed">Failed</option>
                    </select>
                    <select value={this.state.searchOfficeLocation} name="searchOfficeLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Offices</option>
                        {this.state.officeLocations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                    <select value={this.state.searchLocation} name="searchLocation" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Surgery Centers</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                    {/*<select value={this.state.searchPerson} name="searchPerson" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <HasRight right="appt:readall">
                            <option value="">Everyone</option>
                        </HasRight>
                        <option value="caller">You</option>
                    </select>*/}
                </div>
                {table}
                &nbsp;
                <div className="action-panel action-panel-noflip">
                    <button className="button push-right text-with-icon" onClick={this.previousPage} disabled={this.state.searchOffset === 0}><i className="icon-angle-left" /><span>Previous</span></button>
                    <button className="button text-with-icon" onClick={this.nextPage} disabled={this.state.appointments.length < PAGE_SIZE}><span>Next</span><i className="icon-angle-right" /></button>
                </div>
            </div>
        );
    }

    private async search() {
        try {
            this.setState({
                isLoading: true
            });

            this.saveSearchState();

            const parameters: string[] = [
                `limit=${PAGE_SIZE}`,
                `offset=${this.state.searchOffset}`,
                "mode=overview",
                `date=${this.state.dateMode}`
            ];
            if (this.state.searchText) {
                parameters.push(`q=${this.state.searchText}`);
            }
            if (this.state.searchLocation) {
                parameters.push(`location=${this.state.searchLocation}`);
            }
            if (this.state.searchOfficeLocation) {
                parameters.push(`office_location=${this.state.searchOfficeLocation}`);
            }
            if (this.state.searchStatus) {
                parameters.push(`status=${this.state.searchStatus}`);
            }
            if (this.state.searchEnd) {
                const beforeDt = DateTime.fromISO(this.state.searchEnd).endOf("day").plus({ millisecond: 1 });
                parameters.push(`before=${beforeDt.toISODate()}`);
            }
            if (this.state.searchStart) {
                parameters.push(`after=${this.state.searchStart}`);
            }

            const results = await V2.get<any[]>(`surgery/summary?${parameters.join("&")}`);
            for (const item of results) {
                item.note = (item.note || "").replace(/[\r\n]+/g, " ");
            }

            this.setState({
                appointments: results,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Failed to search"
            });
        }
    }

    private statusToClass(status: number) {
        switch (status) {
            case 6: return "row-gray";
            case 5: return "row-orange";
            case 4: return "row-green";
            case 3: return "row-yellow";
            case 2: return "row-red";
            case 1:
            case 0:
            default: return "";
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            searchOffset: 0,

            [name]: value
        }, () => this.search());
    }

    private saveSearchState() {
        const location = this.props.location;
        location.state = {
            searchLocation: this.state.searchLocation,
            searchOfficeLocation: this.state.searchOfficeLocation,
            searchOffset: this.state.searchOffset,
            searchPerson: this.state.searchPerson,
            searchStatus: this.state.searchStatus,
            searchText: this.state.searchText,

            searchEnd: this.state.searchEnd,
            searchStart: this.state.searchStart,
            dateMode: this.state.dateMode
        };

        this.props.history.replace(location);
    }

    private nextPage() {
        this.setState(s => {
            return {
                searchOffset: s.searchOffset + PAGE_SIZE
            };
        }, () => this.search());
    }

    private previousPage() {
        this.setState(s => {
            return {
                searchOffset: Math.max(s.searchOffset - PAGE_SIZE, 0)
            };
        }, () => this.search());
    }
}

export default withAuth(withTitle("Case List", SurgeryColorListView));
